import { createSelector } from 'reselect'
import flow from 'lodash/flow'
import pick from 'lodash/pick'
import sumBy from 'lodash/sumBy'
import max from 'lodash/max'
import range from 'lodash/range'
import { Goal, config } from 'cmw-goaltools'
import indianize from 'utils/indianize'
import summarize from 'utils/summarize'
import properCase from 'utils/properCase'
const { returnProfiles } = config

export const getRegularInvestments = createSelector(
  state => state,
  flow([
    state =>
      Object.values(state.goals).map(goal => ({
        ...goal,
        regularInvestment: Goal({
          age: state.profile.age,
          ...goal,
          assetClassReturns: state.globalSettings.assetClassReturns,
        }).regularInvestment,
        maxStartingAllocation: Goal({
          age: state.profile.age,
          ...goal,
          assetClassReturns: state.globalSettings.assetClassReturns,
        }).maxStartingAllocation,
        active: goal.id === state.activeGoal,
      })),
    goals =>
      goals.map(goal =>
        pick(goal, [
          'id',
          'title',
          'icon',
          'startingAllocation',
          'regularInvestment',
          'maxStartingAllocation',
          'active',
          'investStartAge',
        ])
      ),
  ])
)

export const getBalanceSavings = createSelector(
  state => state,
  state =>
    (
      (state.profile.savings -
        sumBy(Object.values(state.goals), 'startingAllocation')) /
      100000
    ).toFixed(2)
)

export const getReturnProfiles = createSelector(
  () => returnProfiles,
  data =>
    Object.keys(data).map(key => {
      const { label, equity, debt } = data[key]
      return {
        label,
        description: `Equity ${equity}% Debt ${debt}%`,
        value: key,
      }
    })
)

export const getPlanParams = createSelector(
  state => state,
  state => pick(state, ['profile', 'goals', 'globalSettings'])
)

export const getPlanTakeaways = createSelector(
  state => state,
  flow([
    state => ({
      age: state.profile.age,
      goals: Object.values(state.goals).filter(
        goal => goal.investStartAge === state.profile.age
      ),
      assetClassReturns: state.globalSettings.assetClassReturns,
    }),
    filteredState =>
      filteredState.goals.map(goal => ({
        stepUp: goal.inflation,
        amount: Goal({
          age: filteredState.age,
          ...goal,
          assetClassReturns: filteredState.assetClassReturns,
        }).regularInvestment,
      })),
    goalTakeaways =>
      goalTakeaways.length === 0
        ? {
            totalRegularInvestment: 0,
            weightedAverageStepUp: 0,
          }
        : {
            totalRegularInvestment: indianize(sumBy(goalTakeaways, 'amount')),
            weightedAverageStepUp: (
              sumBy(goalTakeaways, o => o.amount * o.stepUp) /
              sumBy(goalTakeaways, 'amount')
            ).toFixed(2),
          },
  ])
)

export const getGoalSummaries = createSelector(
  state => state,
  flow([
    state =>
      Object.values(state.goals).map(goal => ({
        ...goal,
        summary: summarize(state.profile.age, goal),
      })),
    goals =>
      goals.map(goal =>
        pick(goal, ['id', 'icon', 'title', 'category', 'summary'])
      ),
  ])
)

export const getGoalPath = createSelector(
  state => state,
  state => {
    const goal = Goal({
      age: state.profile.age,
      ...state.goals[state.activeGoal],
      assetClassReturns: state.globalSettings.assetClassReturns,
    })
    const regularInvestments = goal.regularInvestments
      .filter(val => val['Monthly Investment'] !== 0)
      .map(val => ({
        Age: val.Age,
        'Monthly Investment': Math.round(val['Monthly Investment'] / 12),
      }))
    return {
      goalPath: goal.goalPath,
      investments:
        regularInvestments.length === 0
          ? [{ Age: state.profile.age, 'Monthly Investment': 0 }]
          : regularInvestments,
    }
  }
)

export const getListOfGoals = createSelector(
  state => Object.values(state.goals),
  flow([
    goals =>
      goals.map(goal => ({ label: properCase(goal.title), value: goal.id })),
    goals => [{ label: 'All goals', value: null }].concat(goals),
  ])
)

export const getPlanPath = createSelector(
  state => state,
  state => {
    const { age, savings } = state.profile
    const goals = Object.values(state.goals)

    if (goals.length === 0) {
      return [{ Age: age, 'Portfolio Value': savings }]
    }

    const goalPaths = goals.map(
      goal =>
        Goal({
          age,
          ...goal,
          assetClassReturns: state.globalSettings.assetClassReturns,
        }).goalPath
    )
    const longestGoalAge = max(goals.map(goal => goal.spendStopAge))
    return range(age, longestGoalAge + 1).reduce((acc, cur) => {
      let totalAtAge = 0
      goalPaths.forEach(goal => {
        const valueAtAge = goal.filter(val => val.Age === cur)
        if (valueAtAge.length === 0) {
          totalAtAge += 0
        } else {
          totalAtAge += valueAtAge[0]['Portfolio Value']
        }
      })
      acc.push({ Age: cur, 'Portfolio Value': totalAtAge })
      return acc
    }, [])
  }
)

export const getPlanInvestmentPath = createSelector(
  state => state,
  state => {
    const { age } = state.profile
    const goals = Object.values(state.goals)

    if (goals.length === 0) {
      return [{ Age: age, 'Monthly Investment': 0 }]
    }

    const goalPaths = goals.map(
      goal =>
        Goal({
          age,
          ...goal,
          assetClassReturns: state.globalSettings.assetClassReturns,
        }).regularInvestments
    )
    const longestGoalAge = max(goals.map(goal => goal.spendStopAge))
    const aggregate = range(age, longestGoalAge + 1)
      .reduce((acc, cur) => {
        let totalAtAge = 0
        goalPaths.forEach(goal => {
          const valueAtAge = goal.filter(val => val.Age === cur)
          if (valueAtAge.length === 0) {
            totalAtAge += 0
          } else {
            totalAtAge += valueAtAge[0]['Monthly Investment']
          }
        })
        acc.push({ Age: cur, 'Monthly Investment': totalAtAge })
        return acc
      }, [])
      .filter(val => val['Monthly Investment'] !== 0)
      .map(val => ({
        Age: val.Age,
        'Monthly Investment': Math.round(val['Monthly Investment'] / 12),
      }))

    if (aggregate.length === 0) return [{ Age: age, 'Monthly Investment': 0 }]
    return aggregate
  }
)



// WEBPACK FOOTER //
// ./src/store/selectors.js